import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { sum } from 'lib/array/arrayUtils'
import { floatify } from 'lib/maths/mathUtils'

export function isPropertyRateLoyaltyPackageUpgradeItem(item: App.Checkout.BaseAccommodationItem<string>): boolean {
  return item.loyaltyAppliedUpgradeType === 'package' && !!item.loyaltyPropertyRateUpgrade
}

/**
 * Whether the item carries a property-rate package upgrade *regardless of applied state
 */
export function hasPropertyRateLoyaltyUpgrade(item: App.Checkout.BaseAccommodationItem<string>): boolean {
  return !!item.loyaltyPropertyRateUpgrade
}

export function getPropertyRateLoyaltyBasePackageUniqueKey(item: App.Checkout.BaseAccommodationItem<string>): string | undefined {
  const source = item.loyaltyPropertyRateUpgrade?.source
  if (!isPropertyRateLoyaltyPackageUpgradeItem(item) || !source?.servicePackageId) return
  return getPackageUniqueKey(source.servicePackageId, item.duration, source.serviceRateId)
}

export function findPropertyRateLoyaltyBasePackage(
  offer: App.Offer,
  item: App.Checkout.LEAccommodationItem,
): App.HotelPackage | undefined {
  const uniqueKey = getPropertyRateLoyaltyBasePackageUniqueKey(item)
  const pkg = uniqueKey ? offer.packages.find(pkg => pkg.uniqueKey === uniqueKey) : undefined
  return pkg && isHotelPackage(pkg) ? pkg : undefined
}

export function getPropertyRatePricingItemTotals(
  pricing: App.Checkout.PropertyRateLoyaltyUpgrade['sourcePricing'],
): App.Checkout.ItemViewTotals {
  return {
    price: pricing.totals.sellingPrice,
    memberPrice: pricing.luxPlusTotals?.sellingPrice ?? 0,
    value: pricing.totals.valuedUpToPrice,
    memberValue: pricing.luxPlusTotals?.valuedUpToPrice ?? 0,
    surcharge: 0,
    taxesAndFees: pricing.totals.taxesAndFees,
    extraGuestSurcharge: 0,
    otherFees: {
      propertyFees: pricing.totals.propertyFees,
    },
    isAvailable: true,
  }
}

export function getPropertyRateUpgradeSourceItemTotals(
  item: App.Checkout.BaseAccommodationItem<string>,
): App.Checkout.ItemViewTotals | undefined {
  const pricing = item.loyaltyPropertyRateUpgrade?.sourcePricing
  return pricing ? getPropertyRatePricingItemTotals(pricing) : undefined
}

/**
 * Returns one side of a loyalty package upgrade's selling price using the customer's price basis.
 * LuxPlus+ subscribers use the member price when available, otherwise the public price.
 * `getPropertyRatePricingItemTotals().memberPrice` cannot be used because it represents a missing
 * member price as `0` for display.
 */
export function getPropertyRatePricingSellingPrice(
  pricing: App.Checkout.PropertyRateLoyaltyUpgrade['sourcePricing'],
  isLuxPlusSubscriber: boolean,
): number {
  const memberPrice = pricing.luxPlusTotals?.sellingPrice
  return isLuxPlusSubscriber && memberPrice ? memberPrice : pricing.totals.sellingPrice
}

/**
 * Calculate the loyalty package upgrade discount sent with a bedbank enquiry.
 *
 * The discount is the difference between the target and source selling prices on the customer's
 * current subscription basis. Bedbank validates margin against that discounted price.
 */
export function getUpgradePricingDiscount(
  upgrade: Pick<App.Checkout.PropertyRateLoyaltyUpgrade, 'sourcePricing' | 'targetPricing'>,
  isLuxPlusSubscriber: boolean,
): number | undefined {
  const { sourcePricing, targetPricing } = upgrade
  if (!targetPricing) return

  const discount = floatify(
    getPropertyRatePricingSellingPrice(targetPricing, isLuxPlusSubscriber) -
    getPropertyRatePricingSellingPrice(sourcePricing, isLuxPlusSubscriber),
  )
  // A negative discount would push the effective price above what we're charging.
  return Math.max(discount, 0)
}

/**
 * Return the quoted total on the customer's current subscription basis.
 *
 * Read this before handling a rejected loyalty package upgrade. Forfeiting it replaces the source
 * price with the target rate's own price and removes the original quote.
 */
export function getQuotedUpgradeTotal(
  itemViews: Array<{ totals: Pick<App.Checkout.ItemViewTotals, 'price' | 'memberPrice'> }>,
  isLuxPlusSubscriber: boolean,
): number {
  return sum(itemViews, ({ totals }) => (isLuxPlusSubscriber && totals.memberPrice) || totals.price)
}

/**
 * Return the payable total and optional comparison for the loyalty package upgrade rejection modal.
 *
 * Bedbank returns only changed figures, so a LuxPlus+ response may contain only the member price.
 * Select the payable figure before building the comparison. Treat a zero member price as unavailable
 * and fall back to the standard price.
 *
 * Returning the comparison values together preserves type narrowing for `<PriceChangeViewV2/>`.
 */
export function getUpgradeRejectionPrices({ quotedPrice, newPrice, newLuxPlusPrice, isLuxPlusSubscriber }: {
  quotedPrice?: number
  newPrice?: number
  newLuxPlusPrice?: number
  isLuxPlusSubscriber: boolean
}): { payablePrice?: number, comparison?: { oldPrice: number, newPrice: number } } {
  const payablePrice = (isLuxPlusSubscriber && newLuxPlusPrice) || newPrice
  return {
    payablePrice,
    comparison: quotedPrice !== undefined && payablePrice !== undefined
      ? { oldPrice: quotedPrice, newPrice: payablePrice }
      : undefined,
  }
}

/**
 * Return the total shown after a rejected loyalty package upgrade is forfeited.
 *
 * A property-fee-only refusal contains no price. Fall back to the target rate's own price on the
 * customer's current subscription basis so the modal can still show the payable total.
 */
export function getForfeitedUpgradeTotals({ price, luxplusPrice, nonDiscountedPrice, isLuxPlusSubscriber }: {
  price?: number
  luxplusPrice?: number
  nonDiscountedPrice: number
  isLuxPlusSubscriber: boolean
}): { newPrice?: number, newLuxPlusPrice?: number } {
  return {
    newPrice: price ?? (isLuxPlusSubscriber ? undefined : nonDiscountedPrice),
    newLuxPlusPrice: luxplusPrice ?? (isLuxPlusSubscriber ? nonDiscountedPrice : undefined),
  }
}

export function getPropertyRateUpgradeDiscount(
  item: App.Checkout.BaseAccommodationItem<string>,
  isLuxPlusSubscriber: boolean,
): number | undefined {
  const upgrade = item.loyaltyPropertyRateUpgrade
  if (!upgrade || !isPropertyRateLoyaltyPackageUpgradeItem(item)) return
  return getUpgradePricingDiscount(upgrade, isLuxPlusSubscriber)
}
